import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Col, Descriptions, Row, Statistic, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '@apis/reservation-success';
import { BATCH_PHASE_LABELS, BATCH_PHASE_ORDER } from '@utils/reservation-success-batch-phases';
import {
  RUN_STATUS_COLOR,
  invocationTypeLabel,
  reservationGradeLabel,
  runStatusLabel,
} from '@utils/reservation-success-labels';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';

const { Paragraph } = Typography;

const ReservationSuccessBatchRunDetailPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['reservation-success-batch-run', runId],
    queryFn: () => api.getReservationSuccessBatchRun(runId!),
    enabled: !!runId,
    refetchInterval: (query) => (query.state.data?.data?.status === 'running' ? 3000 : false),
  });

  const run = data?.data;

  if (isLoading || !run) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  const grades = run.summary?.gradeDistribution || {};
  const phaseDurations = run.summary?.phaseDurationsMs;
  const phaseRows: Array<{ key: string; label: string; ms: number; pct: number | null }> = BATCH_PHASE_ORDER.filter(
    (p) => phaseDurations?.[p] != null,
  ).map((phase) => ({
    key: phase,
    label: BATCH_PHASE_LABELS[phase] || phase,
    ms: phaseDurations![phase],
    pct: run.durationMs ? Math.round((phaseDurations![phase] / run.durationMs) * 100) : null,
  }));
  const knownPhaseSum = phaseRows.reduce((sum, row) => sum + row.ms, 0);
  const otherMs = run.durationMs && run.status !== 'running' ? Math.max(0, run.durationMs - knownPhaseSum) : 0;
  if (otherMs > 0 && run.durationMs) {
    phaseRows.push({
      key: 'other',
      label: '기타',
      ms: otherMs,
      pct: Math.round((otherMs / run.durationMs) * 100),
    });
  }

  return (
    <div style={{ padding: 24 }}>
      <ReservationSuccessPageHeader
        title={`배치 실행 ${run.runId.slice(0, 8)}`}
        parent={{ label: '배치 실행', to: '/reservation-success-batch-runs' }}
        description="이 실행의 구간별 소요와 등급 분포입니다. 진행 중이면 3초마다 갱신됩니다."
        extra={<Button onClick={() => navigate('/reservation-success-batch-runs')}>← 목록</Button>}
      />
      <Tag color={RUN_STATUS_COLOR[run.status]}>{runStatusLabel(run.status)}</Tag>

      <Descriptions bordered column={2} style={{ marginTop: 16, marginBottom: 16 }}>
        <Descriptions.Item label="기준 시간">{run.hourBucket}</Descriptions.Item>
        <Descriptions.Item label="호출">{invocationTypeLabel(run.invocationType)}</Descriptions.Item>
        <Descriptions.Item label="시작">{dayjs(run.startedAt).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
        <Descriptions.Item label="완료">
          {run.completedAt ? dayjs(run.completedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="소요">
          {run.durationMs ? `${Math.round(run.durationMs / 1000)}초` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="스냅샷">{run.summary?.snapshotsWritten ?? '-'}</Descriptions.Item>
      </Descriptions>

      {phaseRows.length > 0 && (
        <Card title="구간별 소요" style={{ marginBottom: 16 }}>
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            dataSource={phaseRows}
            columns={[
              { title: '구간', dataIndex: 'label' },
              {
                title: '소요',
                dataIndex: 'ms',
                render: (ms: number) => `${Math.round(ms / 1000)}초 (${ms.toLocaleString()}ms)`,
              },
              {
                title: '전체 대비',
                dataIndex: 'pct',
                render: (pct: number | null) => (pct != null ? `${pct}%` : '-'),
              },
            ]}
          />
          {run.status === 'running' && (
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              진행 중…
            </Paragraph>
          )}
        </Card>
      )}

      {run.summary && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic title="파트너" value={run.summary.partnerCount} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="회차" value={run.summary.sessionCount} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="상품" value={run.summary.productCount} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="스냅샷" value={run.summary.snapshotsWritten} />
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        {Object.entries(grades).map(([grade, count]) => (
          <Col key={grade} span={6}>
            <Card>
              <Statistic title={reservationGradeLabel(grade)} value={count} />
            </Card>
          </Col>
        ))}
      </Row>

      {run.llmStats && (
        <Card title="진단 호출" style={{ marginBottom: 16 }}>
          <Paragraph>
            성공 {run.llmStats.success} / 대체 {run.llmStats.fallback} / 실패 {run.llmStats.fail}
          </Paragraph>
        </Card>
      )}

      {run.alertStats && (
        <Card title="알림" style={{ marginBottom: 16 }}>
          <Paragraph>
            발송 {run.alertStats.sent} / 생략 {run.alertStats.skipped} / 실패 {run.alertStats.failed}
          </Paragraph>
        </Card>
      )}

      {run.error && (
        <Card title="오류" style={{ marginBottom: 16 }}>
          <Paragraph type="danger">{run.error}</Paragraph>
        </Card>
      )}

      {run.logs && run.logs.length > 0 && (
        <Card title="로그">
          {run.logs.map((log, i) => (
            <Paragraph key={i} style={{ marginBottom: 4 }}>
              {log}
            </Paragraph>
          ))}
        </Card>
      )}
    </div>
  );
};

export default ReservationSuccessBatchRunDetailPage;
